// File: src/pages/ViewOnewarehouse.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";

import { FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";

// ---- API layer (same style you used in other pages) ----
import { _Get } from "../import/ApiEndPoint/ApiCalling";
import { exportDataToPDF } from "../import/Downloader";
import { View_Wareahouse_Byid } from "../import/ApiEndPoint/Api";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("wh-stock-inline-css")) return;
  const style = document.createElement("style");
  style.id = "wh-stock-inline-css";
  style.innerHTML = `
    .whs-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .whs-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .whs-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:0; }
    .whs-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .whs-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .whs-table tbody tr:hover{ background:#f9fbff; }

    /* Column chooser list boxes */
    .listbox {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      max-height: 280px;
      overflow: auto;
      background: #fff;
      padding: 8px;
    }
    .pick-item {
      padding: 8px 10px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      margin-bottom: 6px;
      background: #fff;
    }
  `;
  document.head.appendChild(style);
};

// ---- helpers ----
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const two = (n) =>
  n === null || n === undefined || isNaN(n) ? "" : Number(n).toFixed(2);

export default function ViewOnewarehouse() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [rows, setRows] = useState([]); // productItems
  const [allRows, setAllRows] = useState([]);
  const [warehouseName, setWarehouseName] = useState("");

  const [search, setSearch] = useState("");

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  useEffect(() => injectOnce(), []);

  // ----- Define columns -----
  const baseColumns = useMemo(
    () => [
      {
        key: "productName",
        header: "PRODUCT NAME",
        get: (r) => r?.productId?.Product_Title,
      },
      {
        key: "hsn",
        header: "HSN CODE",
        get: (r) => r?.productId?.HSN_Code,
      },
      {
        key: "currentStock",
        header: "CURRENT STOCK",
        get: (r) => r?.currentStock,
      },
      {
        key: "pendingStock",
        header: "PENDING STOCK",
        get: (r) => r?.productId?.pendingQty,
      },
      {
        key: "totalPrice",
        header: "TOTAL PRICE",
        get: (r) => two(r?.totalPrice),
      },
    ],
    []
  );

  // Column map
  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    return map;
  }, [baseColumns]);

  // Restore or default columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("WarehouseStockCols");
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  // ----- Fetch data -----
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        if (!id) throw new Error("Missing route id");
        const res = await _Get(View_Wareahouse_Byid, id);
        const w = res?.Warehouse || {};
        const items = Array.isArray(w?.productItems) ? w.productItems : [];
        setWarehouseName(w?.warehouseName || "");
        setRows(items);
        setAllRows(items);
      } catch (e) {
        console.error("[ViewOnewarehouse] load error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load warehouse stock."
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // ----- Visible columns & search -----
  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      visibleCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term))
    );
  }, [rows, search, visibleCols]);

  // ----- Export -----
  const handleExportPDF = async () => {
    const header = visibleCols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      visibleCols
        .map((c) => {
          const v = toStr(c.get(r)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, `${warehouseName || "Warehouse"}-Stock`);
  };

  const handleExportXLSX = async (fileName) => {
    const data = filteredRows.map((r) => {
      const obj = {};
      visibleCols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  // ----- Column Chooser Modal logic -----
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("WarehouseStockCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  // ----- Loading -----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {/* Error banner if fetch failed */}
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="whs-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                {warehouseName || "Warehouse"} Stock{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search stock..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <Button
                color="secondary"
                style={{ height: 32 }}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              <div
                onMouseEnter={() => setDownloadOpen(true)}
                onMouseLeave={() => setDownloadOpen(false)}
                style={{ position: "relative" }}
              >
                <ImDownload
                  style={{ cursor: "pointer" }}
                  title="download file"
                  size={24}
                  color="rgb(8, 91, 245)"
                />
                {downloadOpen && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 32,
                      zIndex: 5,
                      border: "1px solid rgb(8, 91, 245)",
                      backgroundColor: "white",
                      borderRadius: 10,
                      overflow: "hidden",
                      minWidth: 130,
                      boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                    }}
                  >
                    <div onClick={handleExportPDF} style={dlItemStyle}>
                      .PDF
                    </div>
                    <div
                      onClick={() =>
                        handleExportXLSX(
                          `${warehouseName || "Warehouse"}-Stock`
                        )
                      }
                      style={dlItemStyle}
                    >
                      .XLSX
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No stock rows found.
              </div>
            ) : (
              <div className="whs-table-wrap">
                <table className="whs-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, i) => (
                      <tr key={r?._id || r?.productId?._id || i}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>{toStr(c.get(r))}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column Chooser Modal */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div className="listbox">
                {baseColumns.map((col) => (
                  <div key={col.key} className="pick-item">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="listbox" style={{ flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        className="pick-item"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
